import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  AlertTriangle, Calendar as CalendarIcon, TrendingUp, DollarSign, Users, Clock,
  ChevronLeft, ChevronRight, MessageCircle, Star, Eye,
  CheckCircle, XCircle, AlertCircle, Check, X as XIcon, MapPin,
  CalendarPlus, Send, ExternalLink, Download,
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const now = new Date();

const getStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (["assigned", "completed", "paid"].includes(s))
    return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20";
  if (["cancelled", "refund", "refunded"].includes(s) || s.includes("cancelled") || s.includes("no show"))
    return "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10";
  if (["pending", "survey pending"].includes(s))
    return "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10";
  return "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-600/10";
};

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tourOffers, setTourOffers] = useState([]);
  const [offerCounters, setOfferCounters] = useState({ accepted: 0, declined: 0, no_response: 0 });
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(now),
    to: endOfMonth(now)
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modalTab, setModalTab] = useState("details");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Get user signup date for calendar restriction
  const [signupDate, setSignupDate] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/summary"),
      api.get("/dashboard/analytics", {
        params: {
          start_date: format(startOfMonth(now), "yyyy-MM-dd"),
          end_date: format(endOfMonth(now), "yyyy-MM-dd")
        }
      }),
      api.get("/tour-offers"),
      api.get("/profile")
    ])
      .then(([s, a, o, p]) => {
        setSummary(s.data);
        setAnalytics(a.data);
        setTourOffers(o.data.offers);
        setOfferCounters(o.data.counters || { accepted: 0, declined: 0, no_response: 0 });
        if (p.data.created_at) {
          setSignupDate(new Date(p.data.created_at));
        }
      })
      .catch(() => toast.error("Failed to load dashboard data"));
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data } = await api.get("/dashboard/analytics", {
        params: {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date: format(dateRange.to, "yyyy-MM-dd")
        }
      });
      setAnalytics(data);
      setShowDatePicker(false);
    } catch {
      toast.error("Failed to load analytics");
    }
  };

  const handleOfferAction = async (offerId, action) => {
    try {
      await api.post(`/tour-offers/${offerId}/action`, { action });
      setTourOffers(prev => prev.map(o => o.id === offerId ? { ...o, status: action } : o));
      setOfferCounters(prev => ({
        ...prev,
        [action === "accepted" ? "accepted" : "declined"]: prev[action === "accepted" ? "accepted" : "declined"] + 1
      }));
      toast.success(action === "accepted" ? t("dashboard.offerAccepted") : t("dashboard.offerDeclined"));
    } catch {
      toast.error("Failed to update offer");
    }
  };

  const openModal = (tour) => {
    setSelected(tour);
    setModalTab("details");
    setMessage("");
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/bookings/${selected.id}/message`, { message });
      toast.success("Message sent successfully!");
      setMessage("");
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleAddToCalendar = (booking, type) => {
    if (type === "google") {
      const time24h = booking.time_24h || "09:00";
      const [hours, mins] = time24h.split(":").map(Number);
      const durationHours = parseInt(booking.duration) || 2;
      const startDate = new Date(booking.date + "T" + time24h + ":00");
      const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

      const formatGCal = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Tour in ${booking.city}`)}&dates=${formatGCal(startDate)}/${formatGCal(endDate)}&details=${encodeURIComponent(`Type: ${booking.type}\nMeeting Point: ${booking.meeting_point}`)}&location=${encodeURIComponent(booking.meeting_point)}`;
      window.open(googleUrl, "_blank");
    } else if (type === "ics") {
      api.get(`/bookings/${booking.id}/ics`).then(({ data }) => {
        const blob = new Blob([data.ics], { type: "text/calendar" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Calendar file downloaded!");
      }).catch(() => toast.error("Failed to generate calendar file"));
    }
  };

  if (!summary || !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1A1B2E] border-t-transparent" />
      </div>
    );
  }

  const tours = summary.active_tours || [];
  const perPage = 5;
  const totalPages = Math.max(1, Math.ceil(tours.length / perPage));
  const displayTours = tours.slice((page - 1) * perPage, page * perPage);

  const tourAnalyticsData = [
    { name: t("dashboard.successfulTours"), value: analytics.tour_analytics.successful_tours, color: "#10B981" },
    { name: t("dashboard.assignedTours"), value: analytics.tour_analytics.assigned_tours, color: "#3B82F6" },
    { name: t("dashboard.pendingSurveys"), value: analytics.tour_analytics.pending_surveys, color: "#F59E0B" },
    { name: t("dashboard.cancelledTours"), value: analytics.tour_analytics.cancelled_tours, color: "#EF4444" },
    { name: t("dashboard.refunds"), value: analytics.tour_analytics.refunds, color: "#8B5CF6" },
  ];

  const canMessage = (tour) => tour.status === "Assigned";
  const pendingOffers = tourOffers.filter(o => o.status === "offered");
  const selectedIsAssigned = selected?.status === "Assigned";

  return (
    <div className="animate-slideUp space-y-6" data-testid="dashboard-page">
      {/* Profile Incomplete Banner */}
      {!summary.profile_complete && (
        <div
          className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl"
          data-testid="profile-incomplete-banner"
        >
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-sm">
            {t("dashboard.profileIncomplete")}{" "}
            <Link to="/profile" className="font-semibold underline hover:no-underline">
              {t("dashboard.here")}
            </Link>{" "}
            {t("dashboard.toComplete")}
          </p>
        </div>
      )}

      <Tabs defaultValue="overview" data-testid="dashboard-tabs">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger
            value="overview"
            data-testid="tab-overview"
            className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white"
          >
            {t("dashboard.overview")}
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            data-testid="tab-analytics"
            className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white"
          >
            {t("dashboard.analytics")}
          </TabsTrigger>
        </TabsList>

        {/* ── Overview Tab ───────────────────────── */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Critical Intel */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.criticalIntel")}</h2>
            <p className="text-sm text-slate-500 mb-4">{t("dashboard.criticalIntelDesc")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: t("dashboard.toursToday"), value: summary.critical_intel.tours_today, icon: CalendarIcon, color: "text-blue-600 bg-blue-50" },
                { label: t("dashboard.toursTomorrow"), value: summary.critical_intel.tours_tomorrow, icon: Clock, color: "text-violet-600 bg-violet-50" },
                { label: t("dashboard.toursNext7Days"), value: summary.critical_intel.tours_next_7_days, icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
              ].map((card, i) => (
                <div
                  key={i}
                  className={`bg-white border border-slate-100 rounded-xl shadow-sm p-6 hover:-translate-y-1 transition-transform duration-200 animate-scaleIn stagger-${i + 1}`}
                  data-testid={`intel-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">{card.label}</span>
                    <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center`}>
                      <card.icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mt-3">{card.value}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Current Month */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.currentMonth")}</h2>
            <p className="text-sm text-slate-500 mb-4">{t("dashboard.currentMonthDesc")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 hover:-translate-y-1 transition-transform duration-200" data-testid="month-tours-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">{t("dashboard.tours")}</span>
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Users className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-slate-900 mt-3">{summary.current_month.tours}</div>
              </div>
              <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 hover:-translate-y-1 transition-transform duration-200" data-testid="month-earnings-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">{t("dashboard.earnings")}</span>
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <DollarSign className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-slate-900 mt-3">
                  EUR {summary.current_month.earnings_estimated}
                </div>
              </div>
            </div>
          </section>

          {/* Tour Offers */}
          <section data-testid="tour-offers-section">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-slate-900">{t("dashboard.tourOffers")}</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">{t("dashboard.tourOffersDesc")}</p>

            {pendingOffers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {pendingOffers.map((offer) => (
                  <div
                    key={offer.id}
                    className="bg-white border border-slate-100 rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow"
                    data-testid={`tour-offer-${offer.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">{offer.city}</h3>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                          {offer.type}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-emerald-600">{offer.compensation}</span>
                    </div>
                    <div className="space-y-1.5 mb-4">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <CalendarIcon className="w-3.5 h-3.5" />
                        <span>{offer.date} at {offer.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{offer.duration} | {offer.participants} participants</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{offer.meeting_point}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleOfferAction(offer.id, "accepted")}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                        data-testid={`accept-offer-${offer.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> {t("dashboard.accept")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOfferAction(offer.id, "declined")}
                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-8 text-xs"
                        data-testid={`decline-offer-${offer.id}`}
                      >
                        <XIcon className="w-3.5 h-3.5 mr-1" /> {t("dashboard.decline")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 text-center text-sm text-slate-500">
                No pending tour offers at the moment.
              </div>
            )}
          </section>

          {/* Active Tours Table */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.active")}</h2>
            <p className="text-sm text-slate-500 mb-4">{t("dashboard.activeDesc")}</p>
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 border-b border-slate-200">
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.city")}</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.date")}</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.time")}</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.language")}</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.status")}</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.payment")}</TableHead>
                    <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.action")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayTours.map((tour, i) => (
                    <TableRow
                      key={tour.id}
                      className="table-row-hover cursor-pointer"
                      data-testid={`tour-row-${tour.id}`}
                      onClick={() => openModal(tour)}
                    >
                      <TableCell className="px-4 py-4 text-sm text-slate-600">{(page - 1) * perPage + i + 1}</TableCell>
                      <TableCell className="px-4 py-4 text-sm font-medium text-slate-900">{tour.city}</TableCell>
                      <TableCell className="px-4 py-4 text-sm text-slate-600">{tour.date}</TableCell>
                      <TableCell className="px-4 py-4 text-sm text-slate-600">{tour.time}</TableCell>
                      <TableCell className="px-4 py-4 text-sm text-slate-600">{tour.language}</TableCell>
                      <TableCell className="px-4 py-4">
                        <span className={`status-badge ${getStatusStyle(tour.status)}`}>{tour.status}</span>
                      </TableCell>
                      <TableCell className="px-4 py-4">
                        <span className={`status-badge ${getStatusStyle(tour.payment_status)}`}>{tour.payment_status}</span>
                      </TableCell>
                      <TableCell className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openModal(tour)}
                            className="text-slate-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-slate-100"
                            data-testid={`view-${tour.id}`}
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className={`transition-colors p-1.5 rounded ${canMessage(tour) ? "text-slate-400 hover:text-blue-600 hover:bg-slate-100" : "text-slate-200 cursor-not-allowed"}`}
                            disabled={!canMessage(tour)}
                            onClick={() => canMessage(tour) && (() => { setSelected(tour); setModalTab("message"); })()}
                            data-testid={`message-${tour.id}`}
                            title={canMessage(tour) ? "Message customer" : "Only for assigned bookings"}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" /> {t("common.previous")}
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          page === i + 1
                            ? "bg-[#1A1B2E] text-white"
                            : "text-slate-500 hover:bg-slate-100"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
                    data-testid="next-page"
                  >
                    {t("common.next")} <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </section>
        </TabsContent>

        {/* ── Analytics Tab ──────────────────────── */}
        <TabsContent value="analytics" className="space-y-6 mt-6">
          {/* Date Range Picker */}
          <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="date-range-picker">
                <CalendarIcon className="w-4 h-4" />
                {format(dateRange.from, "MMM d, yyyy")} — {format(dateRange.to, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-4">
                <p className="text-xs text-slate-500 mb-2">Click to select start date, then end date</p>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => range && setDateRange(range)}
                  numberOfMonths={2}
                  disabled={(date) => signupDate ? date < signupDate : false}
                  fromDate={signupDate || undefined}
                />
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => setShowDatePicker(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={fetchAnalytics} data-testid="apply-date-range">
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tour Requests */}
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 animate-scaleIn" data-testid="tour-requests-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.tourRequests")}</h3>
              <p className="text-sm text-slate-500 mb-4">{t("dashboard.tourRequestsDesc")}</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">{t("dashboard.toursOffered")}</span>
                  <span className="text-lg font-bold text-slate-900">{analytics.tour_requests.tours_offered}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                    <span className="text-lg font-bold text-emerald-700">{analytics.tour_requests.tours_accepted}</span>
                    <p className="text-xs text-emerald-600">{t("dashboard.accepted")}</p>
                    <p className="text-xs text-emerald-500">{analytics.tour_requests.tours_accepted_pct}%</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                    <span className="text-lg font-bold text-red-700">{analytics.tour_requests.tours_declined}</span>
                    <p className="text-xs text-red-600">{t("dashboard.declined")}</p>
                    <p className="text-xs text-red-500">{analytics.tour_requests.tours_declined_pct}%</p>
                  </div>
                  <div className="text-center p-3 bg-slate-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                    <span className="text-lg font-bold text-slate-700">{analytics.tour_requests.no_response}</span>
                    <p className="text-xs text-slate-600">{t("dashboard.noResponse")}</p>
                    <p className="text-xs text-slate-500">{analytics.tour_requests.no_response_pct}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tour Analytics - Bar Chart */}
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 animate-scaleIn stagger-1" data-testid="tour-analytics-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.tourAnalytics")}</h3>
              <p className="text-sm text-slate-500 mb-4">{t("dashboard.tourAnalyticsDesc")}</p>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tourAnalyticsData} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {tourAnalyticsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Average Rating */}
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 animate-scaleIn stagger-2" data-testid="avg-rating-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.averageRating")}</h3>
              <p className="text-sm text-slate-500 mb-4">{t("dashboard.averageRatingDesc")}</p>
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-slate-900">{analytics.average_rating.value}</span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-5 h-5 ${
                        s <= Math.round(analytics.average_rating.value)
                          ? "text-amber-400 fill-amber-400"
                          : "text-slate-200"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-slate-400">({analytics.average_rating.total_reviews} reviews)</span>
              </div>
            </div>

            {/* Earnings */}
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 animate-scaleIn stagger-3" data-testid="earnings-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{t("dashboard.earningsTitle")}</h3>
              <p className="text-sm text-slate-500 mb-4">{t("dashboard.earningsDesc")}</p>
              <div className="text-4xl font-bold text-slate-900">
                EUR {analytics.earnings.total.toLocaleString()}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Booking Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dashboard-booking-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">
                  {selected.city} | {selected.duration} | {selected.date} | {selected.time}
                </DialogTitle>
                <DialogDescription className="sr-only">Booking details</DialogDescription>
              </DialogHeader>

              <Tabs value={modalTab} onValueChange={setModalTab}>
                <TabsList className="bg-slate-100 p-1 rounded-lg">
                  <TabsTrigger value="details" className="rounded-md text-sm" data-testid="modal-tab-details">Details</TabsTrigger>
                  <TabsTrigger
                    value="message"
                    className={`rounded-md text-sm ${!selectedIsAssigned ? "opacity-50" : ""}`}
                    data-testid="modal-tab-message"
                    disabled={!selectedIsAssigned}
                  >
                    Message Customer
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">Status</span>
                        <div className="mt-1"><span className={`status-badge ${getStatusStyle(selected.status)}`}>{selected.status}</span></div>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">Type</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.type}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">Language</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.language}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">Participants</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.participants}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">Meeting Point</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.meeting_point}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">Compensation</span>
                        <p className="text-sm font-semibold text-slate-900 mt-1">{selected.compensation}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {selectedIsAssigned ? (
                        <>
                          <h4 className="text-sm font-semibold text-slate-900 mb-2">Customer Information</h4>
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400">Name</span>
                            <p className="text-sm text-slate-900 mt-1">{selected.customer?.name}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400">Phone</span>
                            <p className="text-sm text-slate-900 mt-1">{selected.customer?.phone}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400">Email</span>
                            <button
                              className="text-sm text-blue-600 hover:underline mt-1 block"
                              onClick={() => setModalTab("message")}
                            >
                              Click to send message
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="p-4 bg-slate-50 rounded-lg text-center">
                          <p className="text-sm text-slate-500">Customer details are only visible for assigned bookings</p>
                        </div>
                      )}

                      {selectedIsAssigned && (
                        <div className="pt-4 space-y-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" className="w-full" data-testid="dashboard-add-to-calendar">
                                <CalendarPlus className="w-4 h-4 mr-2" /> Add to my calendar
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleAddToCalendar(selected, "google")}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Add to Google Calendar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddToCalendar(selected, "ics")}>
                                <Download className="w-4 h-4 mr-2" />
                                Download .ics file
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="message" className="mt-4 space-y-4">
                  {selectedIsAssigned ? (
                    <>
                      <div>
                        <span className="text-sm font-medium text-slate-700">Write your message:</span>
                        <Textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                          placeholder="Type your message..."
                          className="mt-2 min-h-[120px]"
                          data-testid="dashboard-message-textarea"
                        />
                        <p className="text-xs text-slate-400 mt-1 text-right">{message.length}/500</p>
                      </div>
                      <Button
                        onClick={handleSendMessage}
                        disabled={sending || !message.trim()}
                        className="bg-[#1A1B2E] hover:bg-[#2D2E45]"
                        data-testid="dashboard-send-message-btn"
                      >
                        <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send"}
                      </Button>
                    </>
                  ) : (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Messaging is only available for assigned bookings</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
